import { ZodTypeAny, z } from 'zod'

const {
  array,
  string,
  object,
  number,
  boolean,
  literal,
  union,
  lazy,
  any,
  tuple,
} = z

export const baseObjectSchema = object({
  _editable: string().optional(),
  _uid: string().uuid(),
  component: string(),
})

// Created a util to pass thru a schema that we want or the placeholder we want to error on and give a custom error message that we can display to the content editors
const refinedSchemaPlaceholderUnion = <Schema extends ZodTypeAny>(
  displayName: string,
  desiredSchema: Schema,
) =>
  union([desiredSchema, string()]).refine(
    (val: z.infer<typeof sectionTextSchema> | string) => val !== 'placeholder',
    { message: `${displayName}: Please replace placeholder value` },
  )

export const prosOrConsSchema = (displayName?: string) =>
  baseObjectSchema.extend({
    ProAndConList: array(
      baseObjectSchema.extend({
        text: refinedSchemaPlaceholderUnion(`${displayName}`, string()),
      }),
    )
      .min(1)
      .max(3),
  })

export const prosAndConsListSchema = (displayName: string) =>
  array(prosOrConsSchema(displayName))
const nullableString = union([string(), z.null()])
const nullableNumber = z.union([z.number(), z.null()])
const imageSchema = z.object({
  id: nullableNumber.optional(),
  alt: nullableString.optional(),
  name: nullableString.optional(),
  focus: nullableString.optional(),
  title: nullableString.optional(),
  filename: nullableString.optional(),
  copyright: nullableString.optional(),
  fieldtype: nullableString.optional(),
})

export const galleryImageSchema = baseObjectSchema.extend({
  image: imageSchema,
})

export const galleryArrSchema = z
  .array(galleryImageSchema)
  .max(20)
  .refine(
    (images) => images.some(({ image }) => image.id || image.id === null),
    {
      message: 'Gallery: must not contain the placeholder image.',
    },
  )

export const videoSchema = baseObjectSchema
  .extend({
    title: string(),
    videoId: string(),
    thumbnail: imageSchema,
    buttonLabel: string(),
  })
  .passthrough()

export const videoArrSchema = array(videoSchema).max(1)

const starRatingValidation = (displayName?: string) =>
  z.number({ coerce: true }).refine((value) => value !== 0, {
    message: `${displayName ? `${displayName}` : ''}: Rating cannot be 0`,
  })

export const richTextSchema = object({
  type: literal('doc'),
  content: array(any()),
})

export const verdictSchema = baseObjectSchema.extend({
  body: refinedSchemaPlaceholderUnion('Verdict', richTextSchema),
})

export const verdictArrSchema = array(verdictSchema).max(1)

export const sectionTextSchema = object({
  type: literal('doc'),
  content: array(any()),
})

export const sectionSchema = (displayName: string) =>
  baseObjectSchema.extend({
    body: refinedSchemaPlaceholderUnion(displayName, sectionTextSchema),
    image: imageSchema,
    quote: string(),
    rating: starRatingValidation(displayName),
  })

export const sectionArrSchema = (displayName: string) =>
  tuple([sectionSchema(displayName)])

export const relatedReviewsSchema: z.ZodSchema = lazy(() =>
  union([array(string()), array(contentSchema)]),
)

export const contentSchema = baseObjectSchema.extend({
  cons: prosAndConsListSchema('Cons'),
  make: string(),
  pros: prosAndConsListSchema('Pros'),
  model: string(),
  title: string(),
  video: videoArrSchema.optional(),
  gallery: galleryArrSchema,
  snippet: string().max(140),
  verdict: verdictArrSchema,
  introText: refinedSchemaPlaceholderUnion('Intro', sectionTextSchema),
  authorName: string(),
  starRating: starRatingValidation('Star Rating'),
  authorImage: imageSchema,
  interiorSection: sectionArrSchema('Interior'),
  performanceSection: sectionArrSchema('Performance'),
  practicalitySection: sectionArrSchema('Practicality'),
  runningCostsSection: sectionArrSchema('Running Costs'),
  shouldShowLatestCars: boolean(),
})

// We've used passthrough here as well because there's a lot of data we don't care to validate
export const storySchema = object({
  name: string(),
  created_at: string().datetime(),
  published_at: string().datetime(),
  id: number(),
  uuid: string().uuid(),
  content: contentSchema,
  first_published_at: string().datetime(),
}).passthrough()
